import React, { createContext, useState } from 'react';
import { useOnMountUnsafe } from '@make-software/tg-mini-app-ui';

import { AuthProviderType } from '@/entities/user-auth-provider';
import { decodeBase64 } from '@/utils';

export interface AuthData {
  initData: string | undefined;
  authProvider: AuthProviderType | undefined;
  error: string | undefined;
}

const emptyAuthData: AuthData = {
  initData: undefined,
  authProvider: undefined,
  error: undefined
};

const AuthDataContext = createContext<AuthData>(emptyAuthData);

interface AuthDataProviderProps {
  children: any;
}

export const INVALID_AUTH_DATA: string = 'Invalid auth data';

export function AuthDataProvider(props: AuthDataProviderProps) {
  const [authData, setAuthData] = useState<AuthData>(emptyAuthData);

  const getAuthDataFromSearchParams = async () => {
    let error: string | undefined = undefined;
    let initData = '';
    let authProvider: AuthProviderType | undefined = undefined;

    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);

    // Handle OAuth error responses (from Discord, etc.)
    if (params.get('error')) {
      const errorCode = params.get('error');
      const errorDescription = params.get('error_description');
      error = errorDescription || errorCode || 'Authentication failed';

      // Clear any existing session data when OAuth error occurs
      localStorage.removeItem('session');
      localStorage.removeItem('auth_provider');

      // Clean up URL params after reading error
      window.history.replaceState({}, document.title, window.location.pathname);

      setAuthData({
        initData: '',
        authProvider: undefined,
        error
      });
      return;
    }

    if (params.get('otp_token')) {
      const otpToken = params.get('otp_token')!;
      localStorage.setItem('session', otpToken);
      localStorage.setItem('auth_provider', AuthProviderType.otp);

      window.history.replaceState({}, document.title, window.location.pathname);

      initData = otpToken;
      authProvider = AuthProviderType.otp;
    }
    else if (params.get('u')) {
      // authentication params in CWM
      initData = params.get('u')!;
      initData = decodeBase64(initData);
      initData = decodeURIComponent(initData);
      authProvider = AuthProviderType.telegram;

      localStorage.setItem('session', initData);
      localStorage.setItem('auth_provider', AuthProviderType.telegram);
      setTimeout(() => {
        setAuthData({
          initData,
          authProvider,
          error
        });
      }, 300);
      return;
    }
    // Handle Telegram authentication
    else if (params.get('auth_date') && params.get('id')) {
      const searchParams: any = {};
      params.forEach((value, key) => {
        if (key === 'id' || key === 'auth_date') searchParams[key] = Number.parseInt(value);
        else searchParams[key] = value;
      });
      initData = JSON.stringify(searchParams);
      authProvider = AuthProviderType.telegram;

      localStorage.setItem('session', initData);
      localStorage.setItem('auth_provider', AuthProviderType.telegram);
      setTimeout(() => {
        setAuthData({
          initData,
          authProvider,
          error
        });
      }, 300);
      return;
    }
    // Handle Discord authentication callback (token-based, similar to Telegram)
    else if (params.get('discord_token') && params.get('discord_user_id')) {
      // Store the discord_token as the session token (will be used as Bearer token)
      const discordToken = params.get('discord_token')!;
      localStorage.setItem('session', discordToken);
      localStorage.setItem('auth_provider', AuthProviderType.discord);

      // Clean up URL params after storing
      window.history.replaceState({}, document.title, window.location.pathname);

      // Set initData to the token for validation
      initData = discordToken;
      authProvider = AuthProviderType.discord;
    }
    // No auth parameters in URL, try to load from localStorage
    else {
      initData = localStorage.getItem('session') || '';
      authProvider = localStorage.getItem('auth_provider') as AuthProviderType || AuthProviderType.telegram;
    }

    // Check if we have valid auth data based on provider
    if (initData.length === 0) {
      error = INVALID_AUTH_DATA;
    } else if (authProvider === 'otp') {
      if (initData.length === 0) {
        error = INVALID_AUTH_DATA;
      }
    } else if (authProvider === AuthProviderType.discord.toString()) {
      // For Discord, the session is a token string (not JSON)
      // Just verify it's not empty - backend will validate the token
      if (initData.length === 0) {
        error = INVALID_AUTH_DATA;
      }
    } else {
      // For Telegram, check for both auth_date and id in JSON
      if (!initData.includes('auth_date') || !initData.includes('id')) {
        error = INVALID_AUTH_DATA;
      }
    }

    setAuthData({
      initData,
      authProvider,
      error
    });
  };

  useOnMountUnsafe(() => getAuthDataFromSearchParams());

  return <AuthDataContext.Provider value={authData}>{props.children}</AuthDataContext.Provider>;
}

export function useAuthData(): AuthData {
  return React.useContext(AuthDataContext);
}

export default AuthDataProvider;
